import { createStructuredSelector } from '@opengov/compose-app/reselect';
import { EOgqlJobState, IOgqlJobResult, IOgqlPollParams, IQueryRequestParams, IQueryState } from '@opengov/ogql-types';

import { getJobResult, getView, getViews, isQueryResult, queueQuery } from './client';
import { composeEffects } from './module';
import { selectIsQueryCancelled, selectUseFastReturn, selectUseQueryService } from './selectors';
import { ogqlClientActionCreators, ogqlState } from './state';
import { getStaticDelay } from './timing';

let queryId: number = 0;

const getNextQueryId = (): string => {
  queryId = queryId + 1;

  return `${queryId}`;
};

export const ogqlEffects = composeEffects('ogql-effects', (compose) => ({
  getViews: compose.effect(async (entityId: number) => {
    ogqlClientActionCreators.loadingViews();
    return getViews({ entityId, limit: 1000 }).then((response) => {
      ogqlClientActionCreators.loadedViews({ views: response.views });
      return response.views;
    });
  }),
  getView: compose.effect(getView),
  query: compose.statefulEffect(
    createStructuredSelector({
      fastReturn: selectUseFastReturn,
      useQueryService: selectUseQueryService
    }),
    async ({ fastReturn, useQueryService }, e: IQueryState): Promise<IOgqlJobResult> => {
      const nextId = getNextQueryId();
      ogqlState.actionCreators.queueQuery({ ...e, id: nextId, fastReturn, useQueryService });
      return queueQuery({ ...(e as IQueryRequestParams), fastReturn, useQueryService }).then(async (result) => {
        if (isQueryResult(result)) {
          ogqlState.actionCreators.queryFinished({ ...e, id: nextId });
          return { id: e.id, state: EOgqlJobState.finished, jobResult: result };
        }

        return ogqlEffects.pollJobResult({
          ...e,
          id: nextId,
          pollAttempts: 0,
          pollDelay: getStaticDelay()
        });
      });
    }
  ),
  isCancelled: compose.statefulEffect(selectIsQueryCancelled, async (isQueryCancelled, id: string) =>
    Promise.resolve(isQueryCancelled(id))
  ),
  pollJobResult: compose.statefulEffect(
    selectIsQueryCancelled,
    async (isCancelled, e: IOgqlPollParams): Promise<IOgqlJobResult> => {
      if (isCancelled(e.id) || e.pollAttempts >= 50) {
        return { id: `${e.id}`, state: EOgqlJobState.cancelled };
      }
      return getJobResult(e).then(async (result) => {
        switch (result.state) {
          case EOgqlJobState.finished:
            ogqlState.actionCreators.queryFinished(e);
            return result;

          case EOgqlJobState.failed:
            ogqlState.actionCreators.queryFailed(e);
            return result;

          default:
            const pollDelay = getStaticDelay();
            return new Promise((resolve: Function) => setTimeout(resolve, pollDelay)).then(async () => {
              return ogqlEffects.pollJobResult({
                ...e,
                pollAttempts: e.pollAttempts + 1,
                pollDelay: pollDelay
              });
            });
        }
      });
    }
  )
}));
